import { z } from 'zod';
import { BuildingIdSchema, OperationIdSchema, PlayerIdSchema } from '../../ids';
import { InventorySchema } from '../../models/inventory';
import { ItemAmountsSchema } from '../../models/items';
import { LotStateSchema, SiteRefSchema } from '../../models/lot';
import { StructureDefIdSchema, StructureModuleIdSchema } from '../../models/town';
import { API_PREFIX, defineEndpoint } from '../endpoint';

const TilePositionSchema = {
  tx: z.number().int().nonnegative().max(255),
  ty: z.number().int().nonnegative().max(255),
};

/** What a project command changed: the lot it stands on (C5). */
const ProjectResultSchema = z.object({
  operationId: OperationIdSchema,
  site: z.object({ kind: z.literal('lot'), lot: LotStateSchema }),
  inventory: InventorySchema,
});

/**
 * Traces a line (BUILD technique 3, plan E11): a palisade along `points` (tiles of the site, each
 * run between two of them horizontal or vertical), cut in segments of its footprint's width, the
 * last one shorter. The segments whose index `gates` lists are its definition's gate instead.
 * Judged segment by segment like any placement (a refusal names the segment); the whole line is
 * one project (its recipe and work the sum of its segments'), by whoever may build on the site.
 */
export const placeLine = defineEndpoint({
  method: 'POST',
  path: `${API_PREFIX}/lines`,
  access: 'session',
  body: z.object({
    operationId: OperationIdSchema,
    kind: StructureDefIdSchema,
    site: SiteRefSchema,
    points: z.array(z.object(TilePositionSchema)).min(2).max(101),
    gates: z.array(z.number().int().nonnegative()).max(100).optional(),
  }),
  response: ProjectResultSchema,
});

/**
 * Adds a module to a building (BUILD technique 1, plan E3), in a town or on a lot, by whoever may
 * build there: a module raised as a project opens one on the building (installed when done); any
 * other is installed at once, paid from the town storage or, on a lot, from the backpack.
 */
export const addModule = defineEndpoint({
  method: 'POST',
  path: `${API_PREFIX}/buildings/:buildingId/modules`,
  access: 'session',
  params: z.object({ buildingId: BuildingIdSchema }),
  body: z.object({ operationId: OperationIdSchema, module: StructureModuleIdSchema }),
  response: ProjectResultSchema,
});

/**
 * Carries goods from the backpack to a project (BUILD technique 6): anyone within reach of its
 * footprint may bring them (giving is free); only what its recipe still lacks is taken. A
 * project whose recipe is complete (and, from E2, its work done) becomes its structure.
 */
export const deliverToProject = defineEndpoint({
  method: 'POST',
  path: `${API_PREFIX}/projects/:projectId/deliver`,
  access: 'session',
  params: z.object({ projectId: BuildingIdSchema }),
  body: z.object({ operationId: OperationIdSchema, items: ItemAmountsSchema }),
  response: ProjectResultSchema,
});

/**
 * Its owner replaces a built segment of a line with its gate or a piece that fits it (plan E11
 * 10b, PZ-1..4): a project raised centred on the segment (a watchtower turned by `rotation`, its
 * way in judged turned; a gate takes the segment's own footprint and turn), the segment standing
 * until it is done; then the segment goes, half its cost back, and the piece stands in its line.
 * Idempotent.
 */
export const replaceSegment = defineEndpoint({
  method: 'POST',
  path: `${API_PREFIX}/buildings/:buildingId/replace`,
  access: 'session',
  params: z.object({ buildingId: BuildingIdSchema }),
  body: z.object({
    operationId: OperationIdSchema,
    kind: StructureDefIdSchema,
    rotation: z.union([z.literal(0), z.literal(1), z.literal(2), z.literal(3)]).default(0),
  }),
  response: ProjectResultSchema,
});

/**
 * Converts a piece standing in a ruin's slot into another kind the slot takes, in its place (plan
 * E12, TI-20, RU-2): the new piece is raised as a project in the slot, judged as if the old one
 * were gone, which stands until it is done and then goes with half its cost back (as a replaced
 * segment, PZ-3; to the company on its lots, LC-8). Asks the right to undo (`dismantle`, LC-7); a
 * storage converted must be empty. Idempotent.
 */
export const convertInSlot = defineEndpoint({
  method: 'POST',
  path: `${API_PREFIX}/buildings/:buildingId/convert`,
  access: 'session',
  params: z.object({ buildingId: BuildingIdSchema }),
  body: z.object({ operationId: OperationIdSchema, kind: StructureDefIdSchema }),
  response: ProjectResultSchema,
});

/**
 * Its owner dismantles a structure of its own town or lot (BUILD technique 9, plan E13), standing
 * by it: half its cost (its base and its modules', DM-1) comes back to the backpack, what does not
 * fit falls on the ground, and a producer's stock comes along. Never the hearth, the main storage
 * or the camp (DM-3), a storage holding anything (DM-2), or one with a module being raised.
 * Idempotent.
 */
export const dismantleBuilding = defineEndpoint({
  method: 'POST',
  path: `${API_PREFIX}/buildings/:buildingId/dismantle`,
  access: 'session',
  params: z.object({ buildingId: BuildingIdSchema }),
  body: z.object({ operationId: OperationIdSchema }),
  response: ProjectResultSchema,
});

/**
 * Its owner gives a project up (BUILD technique 9): part of what was carried comes back to the
 * backpack (what does not fit falls on the ground), the rest is lost.
 */
export const cancelProject = defineEndpoint({
  method: 'POST',
  path: `${API_PREFIX}/projects/:projectId/cancel`,
  access: 'session',
  params: z.object({ projectId: BuildingIdSchema }),
  body: z.object({ operationId: OperationIdSchema }),
  response: ProjectResultSchema,
});

/**
 * The chronicle of a work (BUILD technique 10, plan E5, CB-3): who raised it, who carried what to
 * it and how much each worked, its modules raised as projects included; a history, never a claim
 * of ownership. Its contributors in the order they first gave.
 */
export const ProjectChronicleSchema = z.object({
  id: BuildingIdSchema,
  kind: StructureDefIdSchema,
  /** Who placed it and when (game time); null for a piece that stood at once. */
  placedBy: PlayerIdSchema.nullable(),
  placedAt: z.number().int().nonnegative().nullable(),
  /** When it was done (game time); null while it is still being raised. */
  completedAt: z.number().int().nonnegative().nullable(),
  /** Its modules done as projects, and when. */
  modules: z.array(
    z.object({ module: StructureModuleIdSchema, completedAt: z.number().int().nonnegative() }),
  ),
  contributors: z.array(
    z.object({
      playerId: PlayerIdSchema,
      name: z.string(),
      /** What it carried to the work. */
      items: ItemAmountsSchema,
      /** The work units it put in. */
      work: z.number().int().nonnegative(),
    }),
  ),
});
export type ProjectChronicle = z.infer<typeof ProjectChronicleSchema>;

/**
 * Reads the chronicle of a project or of a structure raised as one (plan E5), standing by it, like
 * a sign (pending the user's confirmation).
 */
export const getChronicle = defineEndpoint({
  method: 'GET',
  path: `${API_PREFIX}/buildings/:buildingId/chronicle`,
  access: 'session',
  params: z.object({ buildingId: BuildingIdSchema }),
  response: ProjectChronicleSchema,
});
